import { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import cn from 'classnames';

import { PAYMENT_FIELDS } from 'constants/formFields';
import { evAddPaymentInstrumentClick } from 'events/account';

import {
  receiveUpdatePaymentError,
  updateAssociatedPayment,
  updateUnassociatedPayment
} from 'actions/account/order';
import OrderCareAddPayment from 'components/account/Order/OrderCareAddPayment';
import { SmallLoader } from 'components/Loader';
import { isValidCreditCardNumber } from 'helpers/PaymentUtils';
import { track } from 'apis/amethyst';
import css from 'styles/containers/account/orderCare.scss';

export class OrderCare extends Component {
  state = {
    selectedPaymentInstrumentId: null,
    unassociatedAddressId: null,
    unassociatedCCReentry: null,
    addNewPaymentVisible: false
  };

  onAddNewPayment = () => {
    this.setState({ addNewPaymentVisible: true });
    track(evAddPaymentInstrumentClick, { sourcePage: 9 });
  }

  onCancelAddNewPayment = () => {
    this.setState({ addNewPaymentVisible: false });
  }

  onConfirmPayment = e => {
    const {
      updateAssociatedPayment,
      updateUnassociatedPayment,
      receiveUpdatePaymentError
    } = this.props;
    const {
      selectedPaymentInstrumentId,
      unassociatedAddressId,
      unassociatedCCReentry
    } = this.state;

    e.preventDefault();
    if (unassociatedAddressId && unassociatedCCReentry) {
      if (isValidCreditCardNumber(unassociatedCCReentry)) {
        updateUnassociatedPayment(unassociatedCCReentry, selectedPaymentInstrumentId, unassociatedAddressId);
      } else {
        receiveUpdatePaymentError();
      }
    } else {
      updateAssociatedPayment(selectedPaymentInstrumentId);
    }
  };

  onReenterCC = e => {
    this.setState({ unassociatedCCReentry: e.currentTarget.value });
  }

  onSelectPaymentOption = ({ currentTarget }) => {
    const instrumentId = currentTarget.id;
    const isValidated = currentTarget.dataset.validated;

    this.setState({ selectedPaymentInstrumentId: instrumentId });
    if (isValidated === 'true') {
      this.setState({
        unassociatedAddressId: null,
        unassociatedCCReentry: null
      });
    } else { // save addressId to state for association api call
      const { order: { order: { shippingAddress: { addressId } } } } = this.props;
      this.setState({ unassociatedAddressId: addressId });
    }
  };

  isSelectedPaymentOption(instrumentId) {
    return instrumentId === this.state.selectedPaymentInstrumentId;
  }

  makeErrorMessage(paymentUpdateError, paymentType, addCreditCardNumber, paymentOptions, testId) {
    if (paymentUpdateError) {
      return (
        <div className={css.errorMsg} data-test-id={testId('submitPreexistingCCError')}>
          <p><strong>Oh No!</strong> There was an issue processing your request.</p>
          <p>Please verify your card information.</p>
        </div>
      );
    }
    let paymentOptionsMsg = 'Please confirm current card or choose a different one to process your order.';
    if (!paymentOptions || !paymentOptions.length) {
      paymentOptionsMsg = 'Please choose a different card to process your order.';
    }
    return (
      <div className={css.errorMsg} data-test-id={testId('defaultPreexistingCCError')}>
        <p><strong>Sorry,</strong> but we were unable to verify your {paymentType} ending in {addCreditCardNumber}.</p>
        <p>{paymentOptionsMsg}</p>
      </div>
    );
  }

  makePaymentConfirmationButton(paymentOptions, isPaymentUpdating, testId) {
    if (paymentOptions && paymentOptions.length) { // check if the current card used on order is the only one on file
      return (
        <button
          type="submit"
          disabled={isPaymentUpdating}
          data-test-id={testId('submitPreexistingCC')}>
          Confirm This Payment</button>
      );
    }
  }

  render() { // @todo check accessibility (eg. tabbing), and analytics events(?)
    const { order: { order: { paymentMethod, paymentOptions }, isPaymentUpdating, paymentUpdateError } } = this.props;
    const { paymentType, addCreditCardNumber } = paymentMethod;
    const { unassociatedCCReentry, addNewPaymentVisible } = this.state;
    const { testId } = this.context;

    if (isPaymentUpdating) {
      return <SmallLoader />;
    }
    if (addNewPaymentVisible) {
      return <OrderCareAddPayment onCancelAddNewPayment={this.onCancelAddNewPayment} />;
    }
    return (
      <div className={css.wrap}>
        {this.makeErrorMessage(paymentUpdateError, paymentType, addCreditCardNumber, paymentOptions, testId)}
        <form method="post" onSubmit={this.onConfirmPayment} data-test-id={testId('selectPreexistingCCForm')}>
          {paymentOptions.map((option, i) => {
            const {
              paymentType,
              paymentInstrumentId,
              addCreditCardNumber,
              fullName,
              expirationMonth,
              expirationYear,
              validated
            } = option;
            return (
              <div key={`${paymentInstrumentId}_${i}`} className={css.paymentOption}>
                <input
                  id={paymentInstrumentId}
                  className={css.selectPaymentRadioBtn}
                  onChange={this.onSelectPaymentOption}
                  data-validated={validated}
                  type="radio"
                  name="paymentOption"
                  checked={this.isSelectedPaymentOption(paymentInstrumentId)}
                  required />
                <label /* if cc is unassociated with shipping address, show cc re-enter prompt input below instead */
                  htmlFor={paymentInstrumentId}
                  className={cn({ [css.labelHidden] : this.isSelectedPaymentOption(paymentInstrumentId) && !validated })}>
                  <span>{paymentType} ending in {addCreditCardNumber}</span>
                </label>
                { /* prompt to re-enter cc */
                  (this.isSelectedPaymentOption(paymentInstrumentId) && !validated) ?
                    <span>
                      <span className={css.reenterCCPrompt}>Re-enter card number ending in {addCreditCardNumber}</span>
                      <input
                        className={cn(css.reenterCCInput, { [css.reenterCCInputError] : paymentUpdateError })}
                        onChange={this.onReenterCC}
                        placeholder="XXXX XXXX XXXX XXXX"
                        type="text"
                        maxLength={PAYMENT_FIELDS.CC.maxLength}
                        value={unassociatedCCReentry ? unassociatedCCReentry : ''}
                        required />
                    </span>
                    : null
                }
                <span className={css.cardFullName}>{fullName}</span>
                <span className={css.cardExpiration}>{expirationMonth}/{expirationYear}</span>
              </div>
            );
          })}
          {this.makePaymentConfirmationButton(paymentOptions, isPaymentUpdating, testId)}
          <button type="button" onClick={this.onAddNewPayment} data-test-id={testId('addNewPayment')}>Add New Payment</button>
        </form>
      </div>
    );
  }
}

const mapStateToProps = state => ({
  order: state.order
});

OrderCare.contextTypes = {
  testId: PropTypes.func.isRequired
};

export default connect(mapStateToProps, {
  updateAssociatedPayment,
  updateUnassociatedPayment,
  receiveUpdatePaymentError
})(OrderCare);
